/**
 * Daily Activity Log Component
 * ============================
 * 
 * Displays a running timeline of all activities for today including:
 * - Tasks scheduled
 * - Action items spawned/updated  
 * - Progress notes captured
 * - Completions with timestamps
 */

import React, { useState, useEffect } from 'react';
import { 
  Clock, CheckCircle, PlayCircle, FileText, RefreshCw,
  ChevronDown, ChevronUp, Activity, Calendar,
  Zap, ArrowRight, SkipForward, Plus,
  AlertTriangle, Target
} from 'lucide-react';

interface ActivityItem {
  id: number;
  activity_date: string;
  activity_time: string;
  activity_timestamp: string;
  activity_type: string;
  activity_category: string;
  item_type: string | null;
  item_id: number | null;
  item_title: string | null;
  parent_template_type: string | null;
  parent_template_id: number | null;
  parent_template_title: string | null;
  description: string | null;
  notes: string | null;
  progress_before: number | null;
  progress_after: number | null;
  progress_delta: number | null;
  status_before: string | null;
  status_after: string | null;
  duration_minutes: number | null;
  scheduled_time_slot: string | null;
  context: string | null;
  mood: string | null;
  energy_level: string | null;
  source: string;
  extra_data: Record<string, unknown> | null;
  created_at: string;
}

interface ActivitySummary {
  date: string;
  total_activities: number;
  tasks_scheduled: number;
  tasks_completed: number;
  action_items_spawned: number;
  action_items_completed: number;
  progress_notes_added: number;
  total_duration_minutes: number;
  by_type: Record<string, number>;
  by_category: Record<string, number>;
}

interface DailyActivityLogProps {
  date?: string;  // YYYY-MM-DD format, defaults to today
  compact?: boolean;
  maxItems?: number;
  showSummary?: boolean;
  onActivityClick?: (activity: ActivityItem) => void;
}

const activityTypeIcons: Record<string, React.ReactNode> = {
  task_scheduled: <Calendar className="w-4 h-4 text-brand" />,
  task_started: <PlayCircle className="w-4 h-4 text-ok" />,
  task_completed: <CheckCircle className="w-4 h-4 text-ok" />,
  task_progress_update: <Activity className="w-4 h-4 text-warn" />,
  task_skipped: <SkipForward className="w-4 h-4 text-warn" />,
  task_added_to_focus: <Target className="w-4 h-4 text-brand" />,
  action_item_spawned: <Zap className="w-4 h-4 text-brand" />,
  action_item_started: <PlayCircle className="w-4 h-4 text-ok" />,
  action_item_completed: <CheckCircle className="w-4 h-4 text-ok" />,
  action_item_progress_update: <Activity className="w-4 h-4 text-warn" />,
  action_item_skipped: <SkipForward className="w-4 h-4 text-warn" />,
  progress_note_added: <FileText className="w-4 h-4 text-brand" />,
  recurring_instance_created: <RefreshCw className="w-4 h-4 text-info" />,
  recurring_instance_skipped: <SkipForward className="w-4 h-4 text-warn" />,
  focus_item_added: <Plus className="w-4 h-4 text-brand" />,
  status_change: <ArrowRight className="w-4 h-4 text-muted" />,
  schedule_created: <Calendar className="w-4 h-4 text-brand" />,
  schedule_modified: <Calendar className="w-4 h-4 text-brand" />,
  motivator_nudge_received: <Zap className="w-4 h-4 text-warn" />,
};

const activityTypeLabels: Record<string, string> = {
  task_scheduled: 'Task Scheduled',
  task_started: 'Task Started',
  task_completed: 'Task Completed',
  task_progress_update: 'Progress Update',
  task_skipped: '⚠️ Task Skipped',
  task_added_to_focus: 'Added to Focus',
  action_item_spawned: 'Action Item Created',
  action_item_started: 'Action Item Started',
  action_item_completed: 'Action Item Completed',
  action_item_progress_update: 'Action Item Progress',
  action_item_skipped: '⚠️ Action Skipped',
  progress_note_added: 'Note Added',
  recurring_instance_created: 'New Instance',
  recurring_instance_skipped: '⚠️ Instance Skipped',
  focus_item_added: 'Added to Today',
  status_change: 'Status Changed',
  schedule_created: 'Schedule Created',
  schedule_modified: 'Schedule Modified',
  motivator_nudge_received: 'Motivation Boost',
};

// Special styling for skipped activities
const isSkippedActivity = (type: string) => 
  type.includes('skipped');

const formatTime = (timeStr: string): string => {
  if (!timeStr) return '';
  // Handle both "HH:MM:SS" and "HH:MM:SS.microseconds" formats
  const parts = timeStr.split('.')[0].split(':');
  if (parts.length >= 2) {
    const hour = parseInt(parts[0]);
    const minute = parts[1];
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute} ${ampm}`;
  }
  return timeStr;
};

// Get time key for grouping (same minute = same group)
const getTimeKey = (timeStr: string): string => {
  if (!timeStr) return '';
  const parts = timeStr.split('.')[0].split(':');
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`; // HH:MM
  }
  return timeStr;
};

// Group activities by time and type
interface ActivityGroup {
  timeKey: string;
  formattedTime: string;
  activities: ActivityItem[];
  activityTypes: Set<string>;
  isSpawnGroup: boolean;  // True if this group is all spawn/create activities
  isFocusGroup: boolean;  // True if all items were added to focus
}

const groupActivities = (activities: ActivityItem[]): ActivityGroup[] => {
  const groups: Map<string, ActivityGroup> = new Map();
  
  activities.forEach(activity => {
    const timeKey = getTimeKey(activity.activity_time);
    
    if (!groups.has(timeKey)) {
      groups.set(timeKey, {
        timeKey,
        formattedTime: formatTime(activity.activity_time),
        activities: [],
        activityTypes: new Set(),
        isSpawnGroup: true,
        isFocusGroup: true
      });
    }
    
    const group = groups.get(timeKey)!;
    group.activities.push(activity);
    group.activityTypes.add(activity.activity_type);
    
    // Check if this is a spawn/create type
    const isSpawnType = activity.activity_type === 'recurring_instance_created' || 
                        activity.activity_type === 'action_item_spawned' ||
                        activity.activity_type === 'focus_item_added';
    const isFocusType = activity.activity_type === 'focus_item_added' ||
                        activity.activity_type === 'task_added_to_focus';
    
    if (!isSpawnType) group.isSpawnGroup = false;
    if (!isFocusType) group.isFocusGroup = false;
  });
  
  return Array.from(groups.values());
};

const ITEMS_PER_PAGE = 10;

// Grouped Activities Component - for collapsed spawn/focus groups
const GroupedActivities: React.FC<{
  group: ActivityGroup;
  onActivityClick?: (activity: ActivityItem) => void;
}> = ({ group, onActivityClick }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Group by item type for summary
  const taskCount = group.activities.filter(a => a.item_type === 'task').length;
  const actionItemCount = group.activities.filter(a => a.item_type === 'action_item').length;
  
  // Get group icon and label
  const getGroupInfo = () => {
    if (group.isFocusGroup) {
      return {
        icon: <Plus className="w-4 h-4 text-brand" />,
        bgColor: 'bg-brand-subtle',
        borderColor: 'border-brand-subtle',
        label: 'Items Added to Today\'s Focus'
      };
    }
    if (group.isSpawnGroup) {
      return {
        icon: <RefreshCw className="w-4 h-4 text-info" />,
        bgColor: 'bg-info/10',
        borderColor: 'border-info/30',
        label: 'New Items Spawned'
      };
    }
    return {
      icon: <Activity className="w-4 h-4 text-muted" />,
      bgColor: 'bg-surface-alt',
      borderColor: 'border-soft',
      label: 'Multiple Activities'
    };
  };
  
  const groupInfo = getGroupInfo();
  
  return (
    <div className={`relative pl-10 rounded-lg p-2 -ml-2 ${groupInfo.bgColor} border ${groupInfo.borderColor}`}>
      {/* Timeline dot */}
      <div className="absolute left-2 top-3 w-4 h-4 rounded-full bg-surface border-2 border-soft flex items-center justify-center">
        {groupInfo.icon}
      </div>
      
      {/* Group header */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-primary">
                {groupInfo.label}
              </span>
              <span className="text-xs px-2 py-0.5 bg-surface rounded-full text-secondary font-medium">
                {group.activities.length} items
              </span>
              {expanded ? (
                <ChevronUp className="w-4 h-4 text-muted" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted" />
              )}
            </div>
            
            {/* Summary when collapsed */}
            {!expanded && (
              <div className="flex items-center gap-2 mt-1">
                {taskCount > 0 && (
                  <span className="text-xs text-muted">
                    📋 {taskCount} task{taskCount > 1 ? 's' : ''}
                  </span>
                )}
                {actionItemCount > 0 && (
                  <span className="text-xs text-muted">
                    ⚡ {actionItemCount} action item{actionItemCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </div>
          
          <div className="text-xs text-muted whitespace-nowrap">
            {group.formattedTime}
          </div>
        </div>
      </button>
      
      {/* Expanded list */}
      {expanded && (
        <div className="mt-3 space-y-2 pl-2 border-l-2 border-soft ml-1">
          {group.activities.map((activity) => (
            <div 
              key={activity.id}
              className={`pl-3 py-1.5 rounded-md transition-colors ${
                onActivityClick ? 'cursor-pointer hover:bg-surface/50' : ''
              }`}
              onClick={() => onActivityClick?.(activity)}
            >
              <div className="flex items-center gap-2">
                {activityTypeIcons[activity.activity_type] || (
                  <div className="w-3 h-3 rounded-full bg-soft" />
                )}
                <span className="text-sm text-secondary truncate">
                  {activity.item_title}
                </span>
                {activity.item_type && (
                  <span className="text-xs px-1.5 py-0.5 bg-surface/60 rounded text-muted">
                    {activity.item_type === 'task' ? '📋' : '⚡'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DailyActivityLog: React.FC<DailyActivityLogProps> = ({
  date,
  compact = false,
  maxItems,
  showSummary = true,
  onActivityClick,
}) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_PAGE);
  const [filter, setFilter] = useState<string>('all'); // all, completed, skipped, notes
  
  const fetchActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      
      const response = await fetch(`/api/orchestrator/activity-log?${params.toString()}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch activity log');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setActivities(data.activities || []);
        setSummary(data.summary || null);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      console.error('Error fetching activity log:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchActivities();
    
    // QUICK CHECK: 30s interval disabled to test if it causes page reload/refresh.
    // Restore when done: uncomment the setInterval/clearInterval and add interval to the return.
    // // Refresh every 30 seconds
    // const interval = setInterval(fetchActivities, 30000);
    
    // Listen for manual refresh events
    const handleRefresh = () => fetchActivities();
    window.addEventListener('activity-log-refresh', handleRefresh);
    
    return () => {
      // clearInterval(interval);
      window.removeEventListener('activity-log-refresh', handleRefresh);
    };
  }, [date]);
  
  // Reset display count when filter changes
  useEffect(() => {
    setDisplayCount(ITEMS_PER_PAGE);
  }, [filter]);
  
  // Filter activities based on selected filter
  const filteredActivities = activities.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'completed') return activity.activity_type.includes('completed');
    if (filter === 'skipped') return isSkippedActivity(activity.activity_type);
    if (filter === 'notes') return activity.activity_type === 'progress_note_added' || activity.notes;
    if (filter === 'focus') {
      // Focus tab shows: focus-related activities + task/action item started/completed
      return activity.activity_type.includes('focus') || 
             activity.activity_type.includes('scheduled') ||
             activity.activity_type === 'task_started' ||
             activity.activity_type === 'task_completed' ||
             activity.activity_type === 'action_item_started' ||
             activity.activity_type === 'action_item_completed' ||
             activity.activity_type === 'task_progress_update' ||
             activity.activity_type === 'action_item_progress_update';
    }
    return true;
  });
  
  const displayActivities = maxItems 
    ? filteredActivities.slice(0, maxItems)
    : filteredActivities.slice(0, displayCount);
    
  const hasMore = filteredActivities.length > displayCount;
  const skippedCount = activities.filter(a => isSkippedActivity(a.activity_type)).length;
  
  if (loading && activities.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-soft p-4">
        <div className="flex items-center justify-center gap-2 text-muted">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading activity log...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-surface rounded-lg border border-bad/30 p-4">
        <div className="flex items-center gap-2 text-bad">
          <span>⚠️ {error}</span>
          <button 
            onClick={fetchActivities}
            className="text-sm underline hover:text-bad"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-surface rounded-lg border border-soft overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 bg-brand-subtle cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-subtle rounded-lg">
            <Clock className="w-5 h-5 text-brand" />
          </div>
          <div>
            <h3 className="font-semibold text-primary">
              Daily Activity Log
            </h3>
            <p className="text-sm text-muted">
              {summary?.total_activities || 0} activities recorded
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading && <RefreshCw className="w-4 h-4 animate-spin text-muted" />}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-muted" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted" />
          )}
        </div>
      </div>
      
      {expanded && (
        <>
          {/* Summary Stats */}
          {showSummary && summary && (
            <div className="grid grid-cols-4 gap-3 p-4 bg-surface-alt border-b border-soft">
              <div className="text-center">
                <div className="text-2xl font-bold text-ok">
                  {summary.tasks_completed}
                </div>
                <div className="text-xs text-muted">Completed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand">
                  {summary.action_items_completed}
                </div>
                <div className="text-xs text-muted">Actions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-brand">
                  {summary.progress_notes_added}
                </div>
                <div className="text-xs text-muted">Notes</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${skippedCount > 0 ? 'text-warn' : 'text-muted'}`}>
                  {skippedCount}
                </div>
                <div className="text-xs text-muted">Skipped</div>
              </div>
            </div>
          )}
          
          {/* Filter Tabs */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-soft overflow-x-auto">
            {[
              { key: 'all', label: 'All', count: activities.length },
              { key: 'completed', label: '✓ Done', count: activities.filter(a => a.activity_type.includes('completed')).length },
              { key: 'skipped', label: '⚠ Skipped', count: skippedCount },
              { key: 'notes', label: '📝 Notes', count: activities.filter(a => a.activity_type === 'progress_note_added' || a.notes).length },
              { key: 'focus', label: '🎯 Focus', count: activities.filter(a => 
                a.activity_type.includes('focus') || 
                a.activity_type.includes('scheduled') ||
                a.activity_type === 'task_started' ||
                a.activity_type === 'task_completed' ||
                a.activity_type === 'action_item_started' ||
                a.activity_type === 'action_item_completed' ||
                a.activity_type === 'task_progress_update' ||
                a.activity_type === 'action_item_progress_update'
              ).length },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  filter === tab.key
                    ? 'bg-brand-subtle text-brand'
                    : 'bg-surface-alt text-secondary hover:bg-surface-alt'
                }`}
              >
                {tab.label} {tab.count > 0 && <span className="ml-1 opacity-70">({tab.count})</span>}
              </button>
            ))}
          </div>
          
          {/* Activity Timeline */}
          <div className="p-4 max-h-96 overflow-y-auto">
            {displayActivities.length === 0 ? (
              <div className="text-center py-8 text-muted">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No activities recorded today</p>
                <p className="text-sm mt-1">Start working on your tasks to see them here!</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-soft" />
                
                {/* Grouped Activity items */}
                <div className="space-y-4">
                  {groupActivities(displayActivities).map((group) => {
                    // If group has multiple spawn-type activities, render as collapsed group
                    if (group.activities.length > 1 && (group.isSpawnGroup || group.isFocusGroup)) {
                      return (
                        <GroupedActivities 
                          key={group.timeKey} 
                          group={group} 
                          onActivityClick={onActivityClick}
                        />
                      );
                    }
                    
                    // Otherwise render individual activities
                    return group.activities.map((activity) => {
                      const isSkipped = isSkippedActivity(activity.activity_type);
                      
                      return (
                        <div 
                          key={activity.id}
                          className={`relative pl-10 rounded-lg p-2 -ml-2 transition-colors ${
                            onActivityClick ? 'cursor-pointer hover:bg-surface-alt' : ''
                          } ${isSkipped ? 'bg-warn/10 border-l-2 border-warn' : ''}`}
                          onClick={() => onActivityClick?.(activity)}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-2 top-3 w-4 h-4 rounded-full flex items-center justify-center ${
                            isSkipped 
                              ? 'bg-warn/10 border-2 border-warn' 
                              : 'bg-surface border-2 border-soft'
                          }`}>
                            {activityTypeIcons[activity.activity_type] || (
                              <div className="w-2 h-2 rounded-full bg-soft" />
                            )}
                          </div>
                          
                          {/* Activity content */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-medium text-sm ${
                                  isSkipped ? 'text-warn' : 'text-primary'
                                }`}>
                                  {activityTypeLabels[activity.activity_type] || activity.activity_type}
                                </span>
                                {activity.source === 'system' && (
                                  <span className="text-xs px-1.5 py-0.5 bg-surface-alt rounded text-muted">
                                    Auto
                                  </span>
                                )}
                              </div>
                              
                              {/* Item title with progress */}
                              {activity.item_title && (
                                <div className="flex items-center gap-2">
                                  <p className={`text-sm truncate ${
                                    isSkipped ? 'text-warn' : 'text-secondary'
                                  }`}>
                                    {activity.item_title}
                                  </p>
                                  {/* Progress badge with change indicator */}
                                  {activity.progress_after !== null && activity.progress_after !== undefined && (
                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                                      activity.progress_after >= 100 
                                        ? 'bg-ok/10 text-ok'
                                        : activity.progress_after >= 50
                                          ? 'bg-brand-subtle text-brand'
                                          : 'bg-surface-alt text-secondary'
                                    }`}>
                                      {activity.progress_before !== null && activity.progress_before !== undefined 
                                        && activity.progress_before !== activity.progress_after ? (
                                        <>
                                          <span className="opacity-60">{activity.progress_before}%</span>
                                          <span>→</span>
                                          <span className="font-semibold">{activity.progress_after}%</span>
                                        </>
                                      ) : (
                                        <span>{activity.progress_after}%</span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Skipped motivation reminder */}
                              {isSkipped && (
                                <div className="mt-2 p-2 bg-warn/10 rounded-lg border border-warn/30">
                                  <p className="text-xs text-warn flex items-start gap-1.5">
                                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                    <span>
                                      {activity.extra_data?.skip_reason
                                        ? String(activity.extra_data.skip_reason)
                                        : "You added this task for a reason. Remember why it matters to you! 💪"}
                                    </span>
                                  </p>
                                  {!!activity.extra_data?.original_motivation && (
                                    <p className="text-xs text-warn mt-1 italic">
                                      Your reason: "{String(activity.extra_data.original_motivation)}"
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              {/* Progress change */}
                              {activity.progress_delta !== null && activity.progress_delta !== 0 && (
                                <p className="text-xs text-muted flex items-center gap-1 mt-0.5">
                                  Progress: {activity.progress_before || 0}% → {activity.progress_after}%
                                  <span className={`font-medium ${activity.progress_delta > 0 ? 'text-ok' : 'text-bad'}`}>
                                    ({activity.progress_delta > 0 ? '+' : ''}{activity.progress_delta}%)
                                  </span>
                                </p>
                              )}
                              
                              {/* Notes */}
                              {activity.notes && !isSkipped && (
                                <p className="text-xs text-muted italic mt-1 line-clamp-2">
                                  "{activity.notes}"
                                </p>
                              )}
                              
                              {/* Extra info badges */}
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {!!activity.extra_data?.is_recurring && (
                                  <span className="text-xs px-1.5 py-0.5 bg-info/10 text-info rounded">
                                    <RefreshCw className="w-3 h-3 inline mr-1" />
                                    Recurring
                                  </span>
                                )}
                                {!!activity.extra_data?.streak_count && Number(activity.extra_data.streak_count) > 1 && (
                                  <span className="text-xs px-1.5 py-0.5 bg-warn/10 text-warn rounded">
                                    🔥 {String(activity.extra_data.streak_count)} day streak
                                  </span>
                                )}
                                {!!activity.extra_data?.streak_broken && (
                                  <span className="text-xs px-1.5 py-0.5 bg-bad/10 text-bad rounded">
                                    💔 Streak broken
                                  </span>
                                )}
                                {activity.duration_minutes && (
                                  <span className="text-xs px-1.5 py-0.5 bg-surface-alt text-secondary rounded">
                                    ⏱️ {activity.duration_minutes}m
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Timestamp */}
                            <div className="text-xs text-muted whitespace-nowrap">
                              {formatTime(activity.activity_time)}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            )}
            
            {/* Load More Button */}
            {!maxItems && hasMore && (
              <div className="text-center mt-4 pt-4 border-t border-soft">
                <button
                  onClick={() => setDisplayCount(prev => prev + ITEMS_PER_PAGE)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand bg-brand-subtle rounded-lg hover:bg-brand-subtle transition-colors"
                >
                  <ChevronDown className="w-4 h-4" />
                  Load More ({filteredActivities.length - displayCount} remaining)
                </button>
              </div>
            )}
            
            {/* Show more indicator for compact mode */}
            {maxItems && filteredActivities.length > maxItems && (
              <div className="text-center mt-4 pt-4 border-t border-soft">
                <span className="text-sm text-muted">
                  +{filteredActivities.length - maxItems} more activities
                </span>
              </div>
            )}
            
            {/* No results message for filtered view */}
            {displayActivities.length === 0 && activities.length > 0 && (
              <div className="text-center py-4 text-muted">
                <p className="text-sm">No {filter} activities found</p>
                <button
                  onClick={() => setFilter('all')}
                  className="text-xs text-brand mt-1 hover:underline"
                >
                  Show all activities
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DailyActivityLog;
